import { z } from 'zod';

import { idSchema, isoDateSchema, jsonMetaSchema, tagsSchema, utmSchema } from './common.schema';

export const ticketStatusSchema = z.enum([
  'new',
  'bot_handling',
  'queued',
  'open',
  'pending',
  'escalated',
  'resolved',
  'closed',
]);
// Тип TicketStatus — единый источник истины в status-machine/ticket-state-machine
// (там же canTransition/assertTicketTransition). Здесь оставлена только zod-схема.

export const ticketPrioritySchema = z.enum(['low', 'medium', 'high', 'urgent']);
export type TicketPriority = z.infer<typeof ticketPrioritySchema>;

export const ticketSchema = z.object({
  id: idSchema,
  workspace_id: idSchema,
  number: z.number().int().positive(),
  contact_id: idSchema,
  channel_id: idSchema,
  group_id: idSchema.nullable().optional(),
  assigned_to: idSchema.nullable().optional(),
  status: ticketStatusSchema,
  priority: ticketPrioritySchema,
  subject: z.string().nullable().optional(),
  tags: tagsSchema.default([]),
  sla_policy_id: idSchema.nullable().optional(),
  sla_deadline: isoDateSchema.nullable().optional(),
  sla_breached: z.boolean(),
  source_utm: utmSchema.optional(),
  source_page_url: z.string().nullable().optional(),
  meta: jsonMetaSchema.default({}),
  bot_session_id: idSchema.nullable().optional(),
  resolved_by_bot: z.boolean(),
  merged_into_ticket_id: idSchema.nullable().optional(),
  first_reply_at: isoDateSchema.nullable().optional(),
  last_message_at: isoDateSchema.nullable().optional(),
  resolved_at: isoDateSchema.nullable().optional(),
  closed_at: isoDateSchema.nullable().optional(),
  created_at: isoDateSchema,
  updated_at: isoDateSchema,
});
export type Ticket = z.infer<typeof ticketSchema>;

export const createTicketSchema = z.object({
  contact_id: idSchema,
  channel_id: idSchema,
  group_id: idSchema.optional(),
  assigned_to: idSchema.optional(),
  subject: z.string().max(500).optional(),
  priority: ticketPrioritySchema.default('medium'),
  tags: tagsSchema.optional(),
  meta: jsonMetaSchema.optional(),
});

export const updateTicketSchema = z
  .object({
    status: ticketStatusSchema,
    priority: ticketPrioritySchema,
    assigned_to: idSchema.nullable(),
    group_id: idSchema.nullable(),
    subject: z.string().max(500).nullable(),
    tags: tagsSchema,
    meta: jsonMetaSchema,
  })
  .partial();

export const assignTicketSchema = z.object({
  user_id: idSchema.nullable(),
  group_id: idSchema.nullable().optional(),
});

export const bulkActionSchema = z.object({
  ids: z.array(idSchema).min(1).max(500),
  action: z.enum([
    'assign',
    'set_status',
    'set_priority',
    'add_tag',
    'remove_tag',
    'merge',
    'close',
  ]),
  value: jsonMetaSchema.optional(),
});

export const ticketFilterQuerySchema = z.object({
  status: z.array(ticketStatusSchema).optional(),
  priority: z.array(ticketPrioritySchema).optional(),
  channel_id: z.array(idSchema).optional(),
  /** Вкладка магазина во входящих; `none` — диалоги без магазина */
  store_id: z.union([idSchema, z.literal('none')]).optional(),
  group_id: z.array(idSchema).optional(),
  assigned_to: z.union([idSchema, z.literal('me'), z.literal('unassigned')]).optional(),
  tag: z.array(z.string()).optional(),
  q: z.string().optional(),
  sla: z.string().optional(),
  saved_view: idSchema.optional(),
  sort: z.string().optional(),
});
